import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Lede } from '@/components/ui/calm';

/**
 * A deck of cards instead of a column of them.
 *
 * A report page used to be six full cards stacked top to bottom, five of
 * which were off screen. Here the cards sit collapsed in a stack on the left
 * (title, one line, a chip) and the one you pick opens on the right at full
 * size. The page stops scrolling; the reader chooses.
 *
 * items: [{ key, title, tab, cadence, sub, eyebrow, chip, foot, render }] - `tab` is
 * the short name on a tab or chip when `title` is an element. `cadence` is
 * the "day by day" half of a name, set under it rather than trailing it.
 * render() returns the
 * card's body. `storageKey` remembers the last opened card per page.
 * The active card wears the bee band on its left edge - the one place the
 * brand's orange appears on a report.
 */
// A deck this short or shorter shows its views as tabs on the panel's edge.
const TABS_MAX = 4;

// A chip may arrive as an element (a dot, a pill) or as plain words; words
// get the compact status pill so they never run into the title beside them.
const Chip = ({ chip }) => (chip == null || chip === false ? null
  : typeof chip === 'string' || typeof chip === 'number'
    ? <span className="whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-[12px] font-medium leading-[18px] text-muted-foreground">{chip}</span>
    : chip);

// What a tab or a chip says. A title that is an element (a headline with a
// figure in it) cannot sit on a tab, and with no eyebrow either the tab came
// out blank: a filled honey square with nothing in it (8 Oct 2026).
const nameOf = (it) => it.tab ?? (typeof it.title === 'string' ? it.title : it.eyebrow);

export function CardDeck({ items, storageKey, className, label = 'Choose a view', open }) {
  const live = (items || []).filter(Boolean);
  // A long stack runs past the screen: an item picked far down opened its
  // card above the fold, out of sight (Customer segments, 13 groups, 3 Nov
  // 2026). Picked from the stack, the card is brought into view when it is not.
  const panelRef = useRef(null);
  const pick = (key) => {
    setActive(key);
    requestAnimationFrame(() => {
      const el = panelRef.current;
      if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const [active, setActive] = useState(() => {
    try { const v = storageKey && localStorage.getItem(`deck:${storageKey}`); if (v && live.some((i) => i.key === v)) return v; } catch {}
    return live[0]?.key || null;
  });
  useEffect(() => {
    if (!live.some((i) => i.key === active) && live[0]) setActive(live[0].key);
  }, [live, active]);
  useEffect(() => {
    try { if (storageKey && active) localStorage.setItem(`deck:${storageKey}`, active); } catch {}
  }, [storageKey, active]);
  // A card elsewhere on the page can send the reader here. `open` carries a
  // nonce so asking for the SAME card twice still works - a reader who scrolled
  // away and clicked the link again expects to be taken back.
  useEffect(() => {
    if (open?.key && live.some((i) => i.key === open.key)) setActive(open.key);
  }, [open?.key, open?.nonce]);  // eslint-disable-line react-hooks/exhaustive-deps
  const current = live.find((i) => i.key === active) || live[0];

  // A WIDE card - a table with ten columns - has to fight a 300px chooser for
  // room and loses, so it scrolls sideways inside a third of the screen. The
  // chooser folds to a row of chips above the panel instead, which costs one
  // line and hands the table the whole width. The reader's own choice wins and
  // is remembered; the automatic fold only happens while they have never
  // chosen, and only on a card that asked for it.
  const [collapsed, setCollapsed] = useState(() => {
    try { const v = storageKey && localStorage.getItem(`deck:${storageKey}:aside`); if (v) return v === 'off'; } catch {}
    return null;
  });
  const chosen = collapsed !== null;
  // Marking each wide card by hand does not scale and I got it wrong the first
  // time: LTV by campaign was tagged and LTV by acquisition source, the table
  // right above it, was not. So the panel MEASURES itself - if anything inside
  // it scrolls sideways in the space it has, the list folds and gives it the
  // width. `wide` stays as a hint for a card that should open folded before
  // anything has rendered.
  const [overflows, setOverflows] = useState(false);
  const folded = chosen ? collapsed : (!!current?.wide || overflows);
  const fold = (next) => {
    setCollapsed(next);
    try { if (storageKey) localStorage.setItem(`deck:${storageKey}:aside`, next ? 'off' : 'on'); } catch {}
  };
  useEffect(() => { setOverflows(false); }, [current?.key]);

  if (!live.length) return null;

  // FOUR OR FEWER VIEWS ARE TABS, NOT A COLUMN.
  //
  // A 300px chooser beside the panel cost a table a quarter of the width to
  // offer four choices that fit on one line - Channel Roles wrapped every
  // channel name and every header to buy a column that was mostly empty. A
  // short deck puts its views on the panel's own top edge as underlined tabs
  // (the Tabs language) and hands the story the full width. Five or more
  // keep the list on the left, where a long set is easier to scan.
  // One card has nothing to switch to: no tab strip, and the panel names it.
  if (live.length === 1) {
    return <div className={className}>{current && <Panel current={current} single />}</div>;
  }
  if (live.length <= TABS_MAX) {
    const tabs = (
      <div role="tablist" aria-label={label}
        className="flex items-end gap-1 overflow-x-auto border-b border-border px-3 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {live.map((it) => {
          const on = it.key === current?.key;
          return (
            <button key={it.key} type="button" role="tab" aria-selected={on} onClick={() => setActive(it.key)}
              title={typeof it.sub === 'string' ? it.sub : undefined}
              // ONE tab look, the report switcher's (ReportTabs): a filled, thickly
              // underlined active tab on a hairline. A thin underline under
              // plain text did not read as tabs at all (owner, 4 Oct 2026).
              className={cn('-mb-px inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-t-md border-b-[3px] px-4 text-[14px] transition-colors duration-fast',
                on ? 'border-honey bg-honey/[0.08] font-semibold text-foreground' : 'border-transparent font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground')}>
              {nameOf(it)}
              {/* A two-line chip (a value over "per month") stood taller than
                  the 48px tab and sat on the panel's top border; a tab takes
                  the one-line form when the item offers it. */}
              <Chip chip={it.chipTab !== undefined ? it.chipTab : it.chip} />
            </button>
          );
        })}
      </div>
    );
    return <div className={className}>{current && <Panel current={current} tabs={tabs} />}</div>;
  }

  if (folded) {
    return (
      <div className={cn('flex flex-col gap-3', className)}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <p className="t-eyebrow !text-[11px] mr-1 text-muted-foreground">{label}</p>
          {live.map((it) => {
            const on = it.key === current?.key;
            return (
              <button key={it.key} type="button" onClick={() => setActive(it.key)} aria-pressed={on}
                title={typeof it.sub === 'string' ? it.sub : undefined}
                className={cn('max-w-[280px] truncate rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
                  on ? 'border-border-strong bg-muted text-foreground' : 'border-border text-muted-foreground hover:border-border-strong hover:text-foreground')}>
                {nameOf(it)}
              </button>
            );
          })}
          <button type="button" onClick={() => fold(false)}
            className="ml-auto inline-flex items-center gap-1.5 text-[13px] font-medium text-muted-foreground hover:text-foreground">
            <PanelLeftOpen className="size-3.5" /> Show the list
          </button>
        </div>
        {current && <Panel current={current} />}
      </div>
    );
  }

  return (
    // The list is as narrow as a title allows: 256px, not 300 - the width
    // belongs to the report on the right.
    <div className={cn('grid gap-4 lg:grid-cols-[256px_minmax(0,1fr)] lg:gap-5', className)}>
      {/* The stack. It needs a heading: the first card opens on load, so
          without one the rail reads as leftover navigation rather than a set
          of views the reader is being offered. The count says how many. */}
      <div className="flex flex-col gap-2 lg:sticky lg:top-20 lg:self-start">
        <div className="flex items-baseline justify-between gap-3 px-1 pb-0.5">
          <p className="t-eyebrow !text-[11px] text-muted-foreground">{label}</p>
          <p className="text-[11px] tabular-nums text-muted-foreground">
            {live.findIndex((i) => i.key === current?.key) + 1} of {live.length}
          </p>
        </div>
        {live.map((it) => {
          const on = it.key === current?.key;
          return (
            <button key={it.key} type="button" onClick={() => pick(it.key)} aria-pressed={on}
              title={typeof it.sub === 'string' ? it.sub : undefined}
              className={cn('group relative w-full rounded-xl border bg-card px-4 py-3.5 text-left transition-[border-color,box-shadow,transform] duration-fast ease-standard',
                on ? 'border-border-strong shadow-sm' : 'border-border hover:border-border-strong hover:-translate-y-px')}>
              {on && <span className="absolute inset-y-3 left-0 w-[3px] rounded-r-full bg-bee" aria-hidden />}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {it.eyebrow && <p className="t-eyebrow !text-[11px]">{it.eyebrow}</p>}
                  {/* The name, then how often it is measured, on its own line.
                      "Revenue and order value, day by day" set as one run of
                      text made the cadence compete with the subject and pushed
                      every card to three wrapped lines. */}
                  <p className={cn('mt-0.5 font-display text-[16px] font-semibold leading-snug', on ? 'text-foreground' : 'text-ink-800 dark:text-ink-200')}>{it.title}</p>
                  {it.cadence && <p className="text-[13px] font-medium leading-snug text-muted-foreground">{it.cadence}</p>}
                  {/* What it is doing right now, when the page says (a list's
                      sync, a message on): its own line, under the name. */}
                  {it.foot && <span className="mt-1.5 block">{it.foot}</span>}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1.5">
                  <Chip chip={it.chip} />
                  <ChevronRight className={cn('size-4 transition-transform', on ? 'text-foreground' : 'text-ink-300 group-hover:text-ink-500')} />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* the open card */}
      <div ref={panelRef} className="min-w-0 scroll-mt-24">
        {current && <Panel current={current} onFold={() => fold(true)}
          onOverflow={chosen ? null : () => setOverflows(true)} />}
      </div>
    </div>
  );
}

function Panel({ current, onFold, onOverflow, tabs, single = false }) {
  const body = useRef(null);
  useEffect(() => {
    if (!onOverflow) return undefined;
    const el = body.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    // A table sits in its own overflow-x container, so the PANEL never reports
    // a scroll - the scroller inside it does. Ask the things that can scroll.
    const check = () => {
      const wide = [...el.querySelectorAll('table, [class*="overflow-x"]')]
        .some((n) => n.scrollWidth > n.clientWidth + 8);
      if (wide) onOverflow();
    };
    const t = setTimeout(check, 260);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [onOverflow, current?.key]);
  return (
        <div className="flex min-w-0 flex-col rounded-lg border border-[hsl(var(--card-border))] bg-card">
          {tabs}
          {/* v3 (8 Oct 2026): the tab or the list already names this card, so
              the panel does not say it again (eyebrow + the same title in 18px
              over a tinted band was three lines before any data). What is new
              stays: the cadence, one line of sub, the controls. */}
          {(() => {
            const named = typeof current.title === 'string' && !single;
            const showHead = !named || current.cadence || current.sub || current.aside || onFold;
            if (!showHead) return null;
            return (
          // Under a tab strip the title needs air: at py-3 it sat against the
          // tabs and read as cramped into them (owner, 25 Oct 2026, What We Found).
          <div className={cn('flex flex-wrap items-center justify-between gap-x-8 gap-y-2 border-b border-[hsl(var(--border-light))] px-6',
            tabs ? 'pb-4 pt-5' : 'py-4')}>
            <div className="min-w-0 flex-1 basis-[240px]">
              {!named && current.eyebrow && <p className="t-eyebrow">{current.eyebrow}</p>}
              {!named && <h3 className="text-[16px] font-semibold leading-tight tracking-[-0.01em] text-foreground">{current.title}</h3>}
              {current.cadence && <p className="text-[13.5px] font-medium leading-snug text-foreground">{current.cadence}</p>}
              {current.sub && <Lede text={current.sub} className="mt-1 max-w-[78ch] text-[13.5px] leading-snug text-muted-foreground" />}
            </div>
            {current.aside && <div className="ml-auto shrink-0">{current.aside}</div>}
            {onFold && (
              <button type="button" onClick={onFold} title="Fold the list into a row of chips and give this card the full width"
                className="shrink-0 self-start rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                <PanelLeftClose className="size-4" />
              </button>
            )}
          </div>
            );
          })()}
          {/* The card stretches to the stack's height, so a short panel can be
              left with slack. The body is a full-height flex column: content
              starts at the top by default (an instruction must sit under the
              heading it belongs to), and a panel that wants to be centred in
              the slack - a chart with nothing above it - says so with `my-auto`
              on its own root. */}
          <div ref={body} className="flex flex-1 flex-col p-6">{current.render()}</div>
        </div>
  );
}

export default CardDeck;
